import { FreeSpinsInfoEvent, SpinResponse, SpecialSymbolGroupType } from '@cgs/data-access';
import {
  CollapseGameStateMachine,
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  IGameStateMachineProvider,
  RESOURCES_COMPONENT_TOKEN,
  ResourcesComponent,
} from '@cgs/machines';
import { Container, SceneObject, TextSceneObject } from '@cgs/wge';

export const SANTA_TOP_MULTIPLIER_COMPONENT_TOKEN = Symbol('SantaTopMultiplierComponent');

const enum SceneNames {
  Multiplier = 'multiplier',
  MultiplierText = 'icon_txt',
}

const enum SceneStates {
  Default = 'default',
  Anim = 'anim',
}

export class SantaTopMultiplierComponent {
  private readonly multiplierScene: SceneObject;
  private readonly multiplierTextNode: TextSceneObject;
  private readonly gameStateMachine: CollapseGameStateMachine<SpinResponse>;
  private currentMultiplier: number = 0;
  private defaultMultiplier: number = 10;

  constructor(container: Container) {
    const slot = container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN).slot;

    this.gameStateMachine = container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine as CollapseGameStateMachine<SpinResponse>;

    const multiplierScene = slot.findById(SceneNames.Multiplier);

    if (!multiplierScene) {
      throw new Error('Multiplier scene not found');
    }

    this.multiplierScene = multiplierScene;
    const multiplierTextNode = multiplierScene.findById<TextSceneObject>(SceneNames.MultiplierText);
    if (!multiplierTextNode) {
      throw new Error('Multiplier text node not found');
    }

    this.gameStateMachine.freeSpinsRecovery.entered.listen(() => {
      this.freeSpinMultiplier();
    });
    this.gameStateMachine.endFreeSpinsPopup.entered.listen(() => {
      this.resetMultiplier();
    });
    this.gameStateMachine.init.entering.listen(() => {
      this.setDefaultMultiplier();
    });
    this.gameStateMachine.idle.entered.listen(() => {
      this.resetMultiplier();
    });
    this.gameStateMachine.stopping.entered.listen(() => {
      this.synchronizeMultiplier();
    });

    this.multiplierTextNode = multiplierTextNode;

    this.updateMultiplierDisplay(this.currentMultiplier);
  }

  public addMultiplier(multiplier: number): void {
    if (
      !this.gameStateMachine.curResponse.isFreeSpins ||
      this.gameStateMachine.curResponse.freeSpinsInfo?.currentFreeSpinsGroup?.name ===
        FreeSpinsInfoEvent.FreeSpinsStarted
    ) {
      return;
    }
    this.currentMultiplier += multiplier;
    this.updateAnimMultiplierDisplay();
  }

  public getMultiplier(): number {
    return this.currentMultiplier;
  }

  private setDefaultMultiplier(): void {
    const multiplier =
      this.gameStateMachine.curResponse.specialSymbolGroups?.find(
        (group) => group.type === SpecialSymbolGroupType.Multiplier
      )?.totalWin ?? this.defaultMultiplier;

    this.currentMultiplier = multiplier;
    this.updateMultiplierDisplay(this.currentMultiplier);
  }

  private synchronizeMultiplier(): void {
    const multiplier = this.gameStateMachine.curResponse.specialSymbolGroups?.find(
      (group) => group.type === SpecialSymbolGroupType.Multiplier
    );
    if (!multiplier || !multiplier.totalWin) {
      return;
    }

    this.currentMultiplier = multiplier.totalWin;

    this.updateMultiplierDisplay(this.currentMultiplier);
  }
  private resetMultiplier(): void {
    this.currentMultiplier = this.defaultMultiplier;
    this.updateMultiplierDisplay(this.currentMultiplier);
  }

  private freeSpinMultiplier(): void {
    const roundStartMultiplier = this.gameStateMachine.curResponse.specialSymbolGroups?.find(
      (group) => group.type === SpecialSymbolGroupType.Multiplier
    );
    if (!roundStartMultiplier || !roundStartMultiplier.totalWin) {
      return;
    }
    this.currentMultiplier = roundStartMultiplier.totalWin;
    this.updateMultiplierDisplay(this.currentMultiplier);
  }

  private updateAnimMultiplierDisplay(): void {
    this.multiplierScene.stateMachine?.switchToState(SceneStates.Default);
    this.multiplierScene.stateMachine?.switchToState(SceneStates.Anim);
    this.updateMultiplierDisplay(this.currentMultiplier);
  }

  private updateMultiplierDisplay(multiplier: number): void {
    this.multiplierTextNode.text = multiplier.toString();
  }
}
